import {
  type RowModel,
  assignTableAPIs,
  functionalUpdate,
  type RowData,
  type Table,
  type TableFeature,
  type Updater,
} from '@tanstack/react-table';

import {
  DATA_TABLE_DEFAULT_PAGE_SIZE_OPTION,
  DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX,
} from '../../constants.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import { createTableConstructor } from '../types/table.types.js';
import type { DataTablePaginationState } from './pagination-types.js';

/**
 * @internal
 */
export function table_getPrePaginatedRowModel<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
): RowModel<TDataTableFeatures, TData> {
  return table.getRowOrderRowModel();
}

/**
 * Helper function to update the pagination state with the provided updater function or value.
 * @internal
 */
export function table_setPagination<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  updater: Updater<DataTablePaginationState>,
  trigger?: 'user' | 'internal',
) {
  const safeUpdater: Updater<DataTablePaginationState> = (old) => {
    const newState: DataTablePaginationState = functionalUpdate(updater, old);

    return newState;
  };

  return table.options.onPaginationChange?.(safeUpdater, trigger);
}

/**
 * Helper function to update the page index with the provided updater function or value.
 * @internal
 */
export function table_setPageIndex<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  updater: Updater<number>,
  trigger?: 'user' | 'internal',
) {
  table_setPagination(
    table,
    (old) => {
      let pageIndex = functionalUpdate(updater, old.pageIndex);

      const maxPageIndex =
        typeof table.options.pageCount === 'undefined' ||
        table.options.pageCount === -1
          ? Number.MAX_SAFE_INTEGER
          : table.options.pageCount - 1;

      pageIndex = Math.max(0, Math.min(pageIndex, maxPageIndex));

      return {
        ...old,
        pageIndex,
      };
    },
    trigger,
  );
}

/**
 * Override of tanstack function in order to call the correct table_setPagination
 */
export function table_setPageSize<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  updater: Updater<number>,
) {
  table_setPagination(table, (old) => {
    const pageSize = Math.max(1, functionalUpdate(updater, old.pageSize));
    const topRowIndex = old.pageSize * old.pageIndex;
    const pageIndex = Math.floor(topRowIndex / pageSize);

    return {
      ...old,
      pageIndex,
      pageSize,
    };
  });
}

export function table_resetPageSize<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  defaultState?: boolean,
) {
  table_setPageSize(
    table,
    defaultState
      ? DATA_TABLE_DEFAULT_PAGE_SIZE_OPTION
      : (table.initialState.pagination?.pageSize ??
          DATA_TABLE_DEFAULT_PAGE_SIZE_OPTION),
  );
}

/**
 * Override of tanstack function in order to call the correct table_setPageIndex
 * @internal
 */
export function table_autoResetPageIndex<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
) {
  if (
    table.options.autoResetAll ??
    table.options.autoResetPageIndex ??
    !table.options.manualPagination
  ) {
    table_resetPageIndex(table);
  }
}
/**
 * Helper function to reset the page index to the default page index.
 * @internal
 */
export function table_resetPageIndex<TData extends RowData>(
  table: Table<TDataTableFeatures, TData>,
  defaultState?: boolean,
) {
  table_setPageIndex(
    table,
    defaultState
      ? DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX
      : (table.initialState?.pagination?.pageIndex ??
          DATA_TABLE_DEFAULT_PAGINATION_PAGE_INDEX),
    'internal',
  );
}

/**
 * Constructs the pagination feature for the table.
 * @internal
 */
export function constructRowPaginationFeature(): TableFeature {
  return {
    constructTableAPIs: createTableConstructor(
      (table: Table<TDataTableFeatures, RowData>) => {
        assignTableAPIs('rowPaginationFeature', table, {
          table_autoResetPageIndex: {
            fn: () => table_autoResetPageIndex(table),
          },
          table_setPagination: {
            fn: (
              updater: Updater<DataTablePaginationState>,
              trigger?: 'user' | 'internal',
            ) => table_setPagination(table, updater, trigger),
          },
          table_setPageIndex: {
            fn: (updater: Updater<number>, trigger?: 'user' | 'internal') =>
              table_setPageIndex(table, updater, trigger),
          },
          table_setPageSize: {
            fn: (updater) => table_setPageSize(table, updater),
          },
          table_resetPageIndex: {
            fn: (defaultState?: boolean) =>
              table_resetPageIndex(table, defaultState),
          },
          table_resetPageSize: {
            fn: (defaultState) => table_resetPageSize(table, defaultState),
          },
          table_getPrePaginatedRowModel: {
            fn: () => table_getPrePaginatedRowModel(table),
          },
        });
      },
    ),
  };
}

/**
 * Feature implementation for row pagination.
 * @internal
 */
export const DataTableRowPaginationFeature = constructRowPaginationFeature();
